import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useFilters } from "../context/FilterContext";
import { useUser } from "../context/UserContext";
import { BrandLogo, companyName } from "../components/BrandLogo";
import { branding } from "../config/branding";
import { motion } from "framer-motion";
import lottie from "lottie-web";
import moneyLoadingAnimation from "../assets/Cash.json";
import { Badge } from "../components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";

interface TrendPoint {
  date: string;
  value: number;
}

interface OfferData {
  _id: string;
  name: string; // Display name (mock in demo mode, real otherwise)
  totalCalls: number;
  totalCash: number;
  totalRevenue: number;
  mrrCash: number;
  callsClosed: number;
  closeRate: number;
  showRate: number;
  avgCashPerCall: number;
  trend: TrendPoint[];
}

function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return "99,102,241";
  const [, r = "63", g = "102", b = "241"] = result;
  return `${parseInt(r, 16)},${parseInt(g, 16)},${parseInt(b, 16)}`;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("en-US").format(value);
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatCompact(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

// SVG donut ring
function ShareRing({ percent, color, size = 42 }: { percent: number; color: string; size?: number }) {
  const strokeW = 3;
  const radius = (size - strokeW) / 2;
  const circumference = 2 * Math.PI * radius;
  const filled = (percent / 100) * circumference;
  const gap = circumference - filled;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="currentColor" className="text-border/20" strokeWidth={strokeW} />
      <circle
        cx={size / 2} cy={size / 2} r={radius}
        fill="none" stroke={color} strokeWidth={strokeW} strokeLinecap="round"
        strokeDasharray={`${filled} ${gap}`}
        strokeDashoffset={circumference / 4}
        style={{ transition: "stroke-dasharray 0.8s ease-out" }}
      />
    </svg>
  );
}

// Mini sparkline chart
function Sparkline({ data, color, width = 80, height = 24 }: { data: TrendPoint[]; color: string; width?: number; height?: number }) {
  if (data.length < 2) return null;
  const values = data.map(d => d.value);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;

  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - 2 - ((v - min) / range) * (height - 4);
    return `${x},${y}`;
  }).join(" ");

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block">
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
        opacity="0.5"
      />
    </svg>
  );
}

function MoneyLoader() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const anim = lottie.loadAnimation({
      container: containerRef.current,
      renderer: "svg",
      loop: true,
      autoplay: true,
      animationData: moneyLoadingAnimation,
    });
    return () => anim.destroy();
  }, []);

  return <div ref={containerRef} style={{ width: 180, height: 180 }} />;
}

export function OfferSelection() {
  const [offers, setOffers] = useState<OfferData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { setOffer, offer } = useFilters();
  const { user, getVisibleOffers } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    const minDelay = new Promise((r) => setTimeout(r, 1800));
    const fetchOffers = async () => {
      try {
        const [res] = await Promise.all([fetch("/api/offers"), minDelay]);
        const data = await res.json();
        if (data.success) {
          setOffers(data.data);
        } else {
          setError("Failed to load offers");
        }
      } catch {
        setError("Failed to load offers");
      } finally {
        setLoading(false);
      }
    };
    fetchOffers();
  }, []);

  const handleSelectOffer = (offerName: string) => {
    setOffer(offerName);
    navigate(`/offer/dashboard?offers=${encodeURIComponent(offerName)}`);
  };

  const handleSelectAll = () => {
    setOffer("all");
    navigate("/agency/dashboard?offers=all");
  };

  const visibleOffers = useMemo(() => {
    if (!user || user.role !== "member") return offers;
    const allowed = new Set(getVisibleOffers(offers.map((entry) => entry.name)));
    return offers.filter((entry) => allowed.has(entry.name));
  }, [getVisibleOffers, offers, user]);

  const totals = useMemo(() => {
    const t = offers.reduce(
      (acc, o) => ({
        totalCalls: acc.totalCalls + o.totalCalls,
        totalCash: acc.totalCash + o.totalCash,
        totalRevenue: acc.totalRevenue + o.totalRevenue,
        mrrCash: acc.mrrCash + (o.mrrCash || 0),
        callsClosed: acc.callsClosed + (o.callsClosed || 0),
      }),
      { totalCalls: 0, totalCash: 0, totalRevenue: 0, mrrCash: 0, callsClosed: 0 }
    );
    return {
      ...t,
      closeRate: t.totalCalls > 0 ? (t.callsClosed / t.totalCalls) * 100 : 0,
      avgCashPerCall: t.totalCalls > 0 ? t.totalCash / t.totalCalls : 0,
    };
  }, [offers]);

  const chartColors = branding.colors.chart;
  const brandRgb = hexToRgb(branding.colors.primary);

  return (
    <TooltipProvider>
      <div className="min-h-screen flex flex-col items-center relative overflow-hidden -m-4 md:-m-8 px-8 sm:px-10 md:px-16 py-14 sm:py-18 md:py-22">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background: `radial-gradient(circle at 18% 12%, rgba(${brandRgb}, 0.18), transparent 38%), radial-gradient(circle at 82% 18%, rgba(${brandRgb}, 0.12), transparent 34%), radial-gradient(circle at 50% 100%, rgba(${brandRgb}, 0.08), transparent 44%)`,
          }}
        />

        {/* Header */}
        <motion.div
          className="text-center mb-14 relative z-10"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <BrandLogo variant="primary" className="h-10 sm:h-14 w-auto mx-auto mb-5" />
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight mb-2">
            Select an Offer
          </h1>
          <p className="text-muted-foreground text-sm">
            Choose which offer to view across all {companyName} dashboards
          </p>
        </motion.div>

        {/* Loading animation */}
        {loading && (
          <motion.div
            className="flex flex-col items-center justify-center py-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <MoneyLoader />
            <p className="text-sm text-muted-foreground mt-2 animate-pulse">Loading offers...</p>
          </motion.div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="text-center py-16">
            <p className="text-destructive mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 rounded-xl bg-[var(--brand-primary)] text-white text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Retry
            </button>
          </div>
        )}

        {/* Content */}
        {!loading && !error && visibleOffers.length > 0 && (
          <div className="w-full max-w-6xl space-y-6 relative z-10">
            {/* Hero: All Offers */}
            {user?.role !== "member" && (
            <motion.button
              onClick={handleSelectAll}
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
              whileHover={{ y: -3, transition: { duration: 0.2 } }}
              className={`group relative w-full text-left rounded-2xl overflow-hidden border transition-colors duration-300 cursor-pointer ${
                offer === "all"
                  ? "border-[var(--brand-primary)]/50"
                  : "border-white/[0.06] hover:border-[var(--brand-primary)]/30"
              }`}
              style={{
                boxShadow: offer === "all"
                  ? `0 0 40px -8px rgba(${brandRgb}, 0.3)`
                  : `0 0 20px -8px rgba(${brandRgb}, 0.1)`,
              }}
            >
              {/* Top accent bar */}
              <div
                className="h-1 w-full"
                style={{ background: `linear-gradient(90deg, ${branding.colors.gradient.start}, ${branding.colors.gradient.end})` }}
              />

              <div
                className="relative z-10 p-7 sm:p-8"
                style={{ background: `linear-gradient(140deg, rgba(${brandRgb}, 0.14), rgba(${brandRgb}, 0.03) 52%, transparent 84%)` }}
              >
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg"
                    style={{ background: `linear-gradient(135deg, ${branding.colors.gradient.start}, ${branding.colors.gradient.end})` }}
                  >
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl font-bold text-foreground">All Offers</h3>
                      {offer === "all" && (
                        <Badge variant="outline" className="border-[var(--brand-primary)]/30 text-[var(--brand-primary)] text-[10px] uppercase tracking-wider">
                          <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-primary)] animate-pulse mr-1" />
                          Active
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{offers.length} offers combined</p>
                  </div>
                </div>

                {/* Metrics Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-x-4 sm:gap-x-5 gap-y-4 sm:gap-y-5 md:gap-x-8">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="cursor-default">
                        <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.08em] font-medium block mb-0.5">Cash Collected</span>
                        <span className="text-base sm:text-xl font-bold text-foreground tabular-nums">{formatCurrency(totals.totalCash)}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>Total cash across all offers</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="cursor-default">
                        <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.08em] font-medium block mb-0.5">Revenue</span>
                        <span className="text-base sm:text-xl font-bold text-foreground tabular-nums">{formatCurrency(totals.totalRevenue)}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>Total revenue generated</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="cursor-default">
                        <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.08em] font-medium block mb-0.5">MRR</span>
                        <span className="text-base sm:text-xl font-bold text-foreground tabular-nums">{formatCurrency(totals.mrrCash)}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>Monthly recurring revenue</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="cursor-default">
                        <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.08em] font-medium block mb-0.5">Total Calls</span>
                        <span className="text-base sm:text-xl font-bold text-foreground tabular-nums">{formatNumber(totals.totalCalls)}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>Total calls across all offers</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="cursor-default">
                        <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.08em] font-medium block mb-0.5">Avg $/Call</span>
                        <span className="text-base sm:text-xl font-bold text-foreground tabular-nums">{formatCompact(totals.avgCashPerCall)}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>Average cash collected per call</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="cursor-default">
                        <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.08em] font-medium block mb-0.5">Closed</span>
                        <span className="text-base sm:text-xl font-bold text-foreground tabular-nums">{formatNumber(totals.callsClosed)}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>Total calls closed with cash</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="cursor-default">
                        <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.08em] font-medium block mb-0.5">Close Rate</span>
                        <span className="text-base sm:text-xl font-bold text-foreground tabular-nums">{formatPercent(totals.closeRate)}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>Percentage of calls that closed</TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </motion.button>
            )}

            {/* Grid: Individual offers */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {visibleOffers.map((o, i) => {
                const color = chartColors[i % chartColors.length] ?? branding.colors.primary;
                const rgb = hexToRgb(color);
                const cashPercent = totals.totalCash > 0 ? (o.totalCash / totals.totalCash) * 100 : 0;
                const isActive = offer === o._id;

                return (
                  <motion.button
                    key={o._id}
                    onClick={() => handleSelectOffer(o._id)}
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{
                      duration: 0.45,
                      delay: 0.08 + i * 0.05,
                      ease: [0.25, 0.46, 0.45, 0.94],
                    }}
                    whileHover={{ y: -4, transition: { duration: 0.2 } }}
                    className={`group relative text-left rounded-[28px] overflow-hidden border transition-colors duration-300 cursor-pointer ${
                      isActive
                        ? "border-[var(--brand-primary)]/50"
                        : "border-white/[0.06] hover:border-white/[0.12]"
                    }`}
                    style={{
                      boxShadow: isActive
                        ? `0 0 40px -8px rgba(${rgb}, 0.4)`
                        : `0 0 20px -8px rgba(${rgb}, 0.12)`,
                    }}
                  >
                    {/* Top accent bar */}
                    <div
                      className="h-1 w-full"
                      style={{ background: `linear-gradient(90deg, ${color}, ${color}88)` }}
                    />

                    <div
                      className="relative z-10 p-6"
                      style={{ background: `linear-gradient(155deg, rgba(${rgb}, 0.11), rgba(${rgb}, 0.03) 52%, transparent 88%)` }}
                    >
                      {/* Header row */}
                      <div className="flex items-center gap-3 mb-4">
                        <div
                          className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 font-bold text-sm shadow-sm"
                          style={{ background: `${color}18`, color }}
                        >
                          {(o.name || o._id).charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-[15px] font-semibold text-foreground truncate">{o.name || o._id}</h3>
                          <p className="text-[11px] text-muted-foreground">
                            {formatNumber(o.totalCalls)} calls &middot; {formatNumber(o.callsClosed || 0)} closed
                          </p>
                        </div>
                        {/* Share ring with tooltip */}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="relative shrink-0 cursor-default">
                              <ShareRing percent={cashPercent} color={color} />
                              <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-foreground/60">
                                {cashPercent.toFixed(0)}%
                              </span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>{cashPercent.toFixed(1)}% of total cash</TooltipContent>
                        </Tooltip>
                      </div>

                      {/* Cash hero + sparkline */}
                      <div className="flex items-end justify-between mb-4 gap-4">
                        <div>
                          <span className="text-2xl sm:text-3xl font-bold tabular-nums" style={{ color }}>
                            {formatCompact(o.totalCash)}
                          </span>
                          <span className="text-[11px] text-muted-foreground ml-1.5">cash collected</span>
                        </div>
                        {o.trend && o.trend.length > 1 && (
                          <Sparkline data={o.trend} color={color} />
                        )}
                      </div>

                      {/* Separator */}
                      <div className="h-px bg-border/20 mb-4" />

                      {/* Metrics grid - 3 cols x 2 rows */}
                      <div className="grid grid-cols-3 gap-x-4 gap-y-2.5">
                        <div>
                          <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.06em] block">Revenue</span>
                          <span className="text-xs font-semibold text-foreground tabular-nums">{formatCompact(o.totalRevenue)}</span>
                        </div>
                        <div>
                          <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.06em] block">Close Rate</span>
                          <span className="text-xs font-semibold text-foreground tabular-nums">{formatPercent(o.closeRate || 0)}</span>
                        </div>
                        <div>
                          <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.06em] block">Show Rate</span>
                          <span className="text-xs font-semibold text-foreground tabular-nums">
                            {formatPercent(o.showRate)}
                          </span>
                        </div>
                        <div>
                          <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.06em] block">MRR</span>
                          <span className="text-xs font-semibold text-foreground tabular-nums">{formatCompact(o.mrrCash || 0)}</span>
                        </div>
                        <div>
                          <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.06em] block">Avg $/Call</span>
                          <span className="text-xs font-semibold text-foreground tabular-nums">
                            {o.totalCalls > 0 ? formatCompact(o.avgCashPerCall) : "\u2014"}
                          </span>
                        </div>
                        <div>
                          <span className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.06em] block">Closed</span>
                          <span className="text-xs font-semibold text-foreground tabular-nums">
                            {formatNumber(o.callsClosed || 0)}
                          </span>
                        </div>
                      </div>

                      {/* Animated progress bar */}
                      <div className="mt-4 h-1 rounded-full bg-white/[0.04] overflow-hidden">
                        <motion.div
                          className="h-full rounded-full"
                          style={{ background: color }}
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.max(cashPercent, 1)}%` }}
                          transition={{ duration: 0.8, delay: 0.3 + i * 0.05, ease: "easeOut" }}
                        />
                      </div>
                    </div>

                    {/* Active badge */}
                    {isActive && (
                      <div className="absolute top-4 right-3 z-20">
                        <Badge variant="outline" className="border-[var(--brand-primary)]/30 text-[var(--brand-primary)] text-[9px] uppercase tracking-wider px-1.5 py-0">
                          <motion.span
                            className="w-1.5 h-1.5 rounded-full bg-[var(--brand-primary)] mr-1"
                            animate={{ opacity: [1, 0.3, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                          />
                          Active
                        </Badge>
                      </div>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty */}
        {!loading && !error && visibleOffers.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-1">
              {user?.role === "member" ? "No offers assigned" : "No offers found"}
            </p>
            <p className="text-sm text-muted-foreground">
              {user?.role === "member"
                ? "Contact admin to assign at least one offer."
                : "Sync data to populate the Cash Source column."}
            </p>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
